import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../../components/Layout';
import { FONTS, currentFont, loadFont, applyFont } from '../../utils/font';
import '../../assets/scss/account.scss';
import '../../assets/scss/v1plus.scss';

const Profile = () => {
    const navigate = useNavigate();
    const [profile, setProfile] = useState({ email: '', name: '', profile_image: '', social_provider: null });
    const [newName, setNewName] = useState('');
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [open, setOpen] = useState(null); // which row is expanded: 'name' | 'password' | 'font' | 'credits'
    const [font, setFont] = useState(currentFont());

    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user_email');
        navigate('/');
    };
    const [loading, setLoading] = useState(true);
    const guest = profile.social_provider === 'guest';
    // Drop only the session token: the guest token stays so the login that follows takes over this progress
    const goJoin = () => {
        localStorage.removeItem('token');
        navigate('/Login', { state: { save: true } });
    };

    const fetchProfile = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get('/api/auth/profile', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setProfile(res.data);
            setNewName(res.data.name);
            setLoading(false);
        } catch (err) {
            console.error(err);
            if (err.response && err.response.status === 401) {
                localStorage.removeItem('token');
                navigate('/Login');
            }
        }
    }, [navigate]);

    useEffect(() => {
        fetchProfile();
    }, [fetchProfile]);


    const handleUpdateProfile = async () => {
        try {
            const token = localStorage.getItem('token');
            await axios.put('/api/auth/profile', { name: newName, profile_image: profile.profile_image }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            alert('이름을 바꿨어요.');
            setOpen(null);
            fetchProfile();
        } catch (err) {
            alert('업데이트 실패');
        }
    };

    const handleChangePassword = async () => {
        try {
            const token = localStorage.getItem('token');
            await axios.post('/api/auth/change-password', { currentPassword, newPassword }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            alert('비밀번호를 바꿨어요.');
            setCurrentPassword('');
            setNewPassword('');
            setOpen(null);
        } catch (err) {
            alert(err.response?.data?.error || '비밀번호 변경 실패');
        }
    };

    const handleDeleteAccount = async () => {
        const ask = guest ? '체험 기록을 모두 지울까요?' : '정말로 탈퇴할까요? 모든 학습 기록이 삭제돼요.';
        if (!window.confirm(ask)) return;
        try {
            await axios.delete('/api/auth/account');
            localStorage.removeItem('token');
            localStorage.removeItem('guest_token');
            alert(guest ? '체험 기록을 지웠어요.' : '탈퇴가 완료됐어요.');
            navigate('/Login');
        } catch (err) {
            alert('삭제하지 못했어요. 잠시 후 다시 시도해 주세요.');
        }
    };

    if (loading) return <Layout containName="account"><div>Loading...</div></Layout>;

    const toggle = (row) => {
        if (row === 'font') FONTS.forEach(f => loadFont(f.id)); // so every option previews in its own font
        setOpen(open === row ? null : row);
    };
    const pickFont = (id) => { applyFont(id); setFont(id); };
    const account = profile.social_provider ? ({ kakao: '카카오로 로그인', apple: 'Apple로 로그인' }[profile.social_provider] || profile.email) : profile.email;

    return (
        <Layout header={{ title: '마이페이지', Backbtn: true, link: '/Main' }} containName="account">
            <div className="my">

                <section className="my-card">
                    <span className="my-avatar" aria-hidden="true">{guest ? '👋' : (profile.name || '?').slice(0, 1)}</span>
                    <div>
                        <strong>{guest ? '체험 중이에요' : profile.name}</strong>
                        <p>{guest ? '가입하면 지금까지의 기록이 그대로 이어져요' : account}</p>
                    </div>
                </section>
                {guest && (
                    <>
                        <button type="button" className="btn auth-btn" onClick={goJoin}>가입하고 기록 저장하기</button>
                        <button type="button" className="login-switch" onClick={handleLogout}>이미 계정이 있어요</button>
                    </>
                )}

                {!guest && (
                    <ul className="my-list">
                        <li>
                            <button type="button" onClick={() => toggle('name')} aria-expanded={open === 'name'}>이름 바꾸기</button>
                            {open === 'name' && (
                                <div className="my-edit">
                                    <input type="text" value={newName} onChange={e => setNewName(e.target.value)} placeholder="새 이름" aria-label="새 이름" />
                                    <button type="button" onClick={handleUpdateProfile}>저장</button>
                                </div>
                            )}
                        </li>
                        {!profile.social_provider && (
                            <li>
                                <button type="button" onClick={() => toggle('password')} aria-expanded={open === 'password'}>비밀번호 바꾸기</button>
                                {open === 'password' && (
                                    <div className="my-edit is-column">
                                        <input type="password" placeholder="현재 비밀번호" aria-label="현재 비밀번호" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} />
                                        <input type="password" placeholder="새 비밀번호 (영문+숫자 6자 이상)" aria-label="새 비밀번호" value={newPassword} onChange={e => setNewPassword(e.target.value)} />
                                        <button type="button" onClick={handleChangePassword}>저장</button>
                                    </div>
                                )}
                            </li>
                        )}
                        <li><button type="button" onClick={handleLogout}>로그아웃</button></li>
                    </ul>
                )}

                <ul className="my-list">
                    <li>
                        <button type="button" onClick={() => toggle('font')} aria-expanded={open === 'font'}>
                            글꼴<span className="my-value">{FONTS.find(f => f.id === font)?.label}</span>
                        </button>
                        {open === 'font' && (
                            <div className="my-fonts" role="radiogroup" aria-label="글꼴">
                                {FONTS.map(f => (
                                    <button type="button" key={f.id} role="radio" aria-checked={font === f.id}
                                        className={font === f.id ? 'is-on' : ''} style={{ fontFamily: f.family }} onClick={() => pickFont(f.id)}>
                                        <strong>{f.label}</strong>
                                        <span>두벅두벅 하루 6분 영단어</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </li>
                    <li><button type="button" onClick={() => navigate('/Comment')}>두벅이 소리함</button></li>
                    <li><button type="button" onClick={() => navigate('/Terms')}>이용약관</button></li>
                    <li><button type="button" onClick={() => navigate('/Privacy')}>개인정보처리방침</button></li>
                    <li>
                        <button type="button" onClick={() => toggle('credits')} aria-expanded={open === 'credits'}>만든 사람들</button>
                        {open === 'credits' && (
                            <div className="v1-credits">
                                두벅 영워드 · 기획·디자인·개발: kim-joy, pmineq<br /> backend : codemodo
                                {/* Required attribution for the CC BY-SA word lists */}
                                단어 목록: New General Service List · TOEIC Service List (Browne, Culligan &amp; Phillips, CC BY-SA 4.0)
                            </div>
                        )}
                    </li>
                </ul>

                <button type="button" className="my-delete" onClick={handleDeleteAccount}>{guest ? '체험 기록 지우기' : '회원 탈퇴'}</button>
            </div>
        </Layout>
    );
};

export default Profile;
